import React, {
  createContext, useContext, useMemo, useState,
} from 'react';
import { Modal, Nav, Stack } from 'rsuite';
import { FaEye } from 'react-icons/fa';
import { OrderListItem } from '../../api/apiTypes';
import { AddressType } from '../../api/address/addressApiTypes';
import OrderAddressEdit from './OrderAddressEdit';

enum Tab {
  DELIVERY = 'DELIVERY',
  BILLING = 'BILLING',
}

function getAddressType(order: OrderListItem, activeTab: Tab): AddressType {
  if (activeTab === Tab.BILLING) {
    return AddressType.BILLING;
  }

  return order.crossDockDelivery ? AddressType.LAST_MILE_DELIVERY : AddressType.DELIVERY;
}

interface AddressEditContext {
  order?: OrderListItem;
  setOrder: (order?: OrderListItem) => void;
}

const Context = createContext<AddressEditContext>({
  order: undefined,
  setOrder: () => {
  },
});

function useAddressEditContext() {
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error('useAddressEditContext must be used within a Provider');
  }

  return context;
}

function Window(): JSX.Element {
  const {
    order, setOrder,
  } = useAddressEditContext();
  const [activeTab, setActiveTab] = useState<Tab>(Tab.DELIVERY);

  const open = !!order;

  function handleClose(): void {
    setOrder(undefined);
  }

  function handleActiveTabChange(tab: Tab): void {
    setActiveTab(tab);
  }

  return (
    <Modal
      open={open}
      onClose={() => handleClose()}
      backdrop
      overflow
    >
      <Modal.Body>
        {order && (
          <Stack spacing={20} direction="column" alignItems="flex-start">
            <Nav activeKey={activeTab} appearance="subtle" onSelect={(tab) => handleActiveTabChange(tab)}>
              <Nav.Item eventKey={Tab.DELIVERY}>Delivery address</Nav.Item>
              <Nav.Item eventKey={Tab.BILLING}>Billing address</Nav.Item>
            </Nav>
            <OrderAddressEdit
              orderId={order.id}
              addressType={getAddressType(order, activeTab)}
              onCancel={() => handleClose()}
            />
          </Stack>
        )}
      </Modal.Body>
    </Modal>
  );
}

interface ProviderProps {
  children: React.ReactNode;
}

function Provider({ children }: ProviderProps): JSX.Element {
  const [order, setOrder] = useState<OrderListItem | undefined>();

  const context: AddressEditContext = useMemo(
    () => ({
      order,
      setOrder,
    }),
    [order, setOrder],
  );

  return (
    <Context.Provider value={context}>
      {children}
    </Context.Provider>
  );
}

interface OpenProps {
  order?: OrderListItem;
}

function Open({ order }: OpenProps): JSX.Element {
  const { setOrder } = useAddressEditContext();

  function handleClick(): void {
    setOrder(order);
  }

  return (
    <FaEye
      onClick={() => handleClick()}
      title="Edit address."
      type="button"
      cursor="pointer"
    />
  );
}

Open.defaultProps = {
  order: undefined,
};

interface Props {
  order?: OrderListItem;
}

export default function AddressEditModal({ order }: Props): JSX.Element {
  return (
    <Provider>
      <Open order={order} />
      <Window />
    </Provider>
  );
}

AddressEditModal.Window = Window;
AddressEditModal.Provider = Provider;
AddressEditModal.Open = Open;
AddressEditModal.defaultProps = {
  order: undefined,
};
